<script lang="ts">
    import { onMount, onDestroy } from "svelte";
    import { EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter, drawSelection } from "@codemirror/view";
    import { EditorState } from "@codemirror/state";
    import { defaultKeymap, historyKeymap, history, indentWithTab } from "@codemirror/commands";
    import { javascript } from "@codemirror/lang-javascript";
    import { syntaxHighlighting, defaultHighlightStyle } from "@codemirror/language";
    import { editor } from "../lib/editor.svelte.ts";
    import { connection } from "../lib/connection.svelte.ts";

    let editorEl: HTMLDivElement | undefined = $state();
    let view: EditorView | null = null;
    let loading = $state(true);
    let initialContent = $state("");

    const arkhamTheme = EditorView.theme({
        "&": {
            background: "#08060300",
            color: "#c0a860",
            fontSize: "0.76rem",
            fontFamily: "'Courier New', Courier, monospace",
            height: "100%",
        },
        ".cm-scroller": {
            overflow: "auto",
            lineHeight: "1.55",
        },
        ".cm-content": {
            padding: "0.4rem 0",
            caretColor: "#c8a840",
        },
        ".cm-gutters": {
            background: "#0d0b0500",
            borderRight: "1px solid #3a2c10",
            color: "#4a3818",
            minWidth: "2.2rem",
        },
        ".cm-lineNumbers .cm-gutterElement": {
            paddingRight: "0.5rem",
            paddingLeft: "0.3rem",
        },
        ".cm-activeLine": {
            background: "rgba(200,168,64,0.05)",
        },
        ".cm-activeLineGutter": {
            background: "rgba(200,168,64,0.07)",
            color: "#7a6030",
        },
        ".cm-selectionBackground": {
            background: "rgba(200,168,64,0.15) !important",
        },
        "&.cm-focused .cm-selectionBackground": {
            background: "rgba(200,168,64,0.18) !important",
        },
        ".cm-cursor, .cm-dropCursor": {
            borderLeftColor: "#c8a840",
        },
        ".cm-focused": {
            outline: "none",
        },
    }, { dark: true });

    function makeExtensions(target: string) {
        return [
            arkhamTheme,
            history(),
            lineNumbers(),
            highlightActiveLine(),
            highlightActiveLineGutter(),
            drawSelection(),
            syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
            ...(target === "verb-editor" ? [javascript()] : []),
            keymap.of([
                { key: "Ctrl-Enter", run: () => { handleCommit(); return true; } },
                { key: "Escape", run: () => { handleAbort(); return true; } },
                indentWithTab,
                ...defaultKeymap,
                ...historyKeymap,
            ]),
            EditorView.lineWrapping,
        ];
    }

    async function handleCommit() {
        const content = view?.state.doc.toString() ?? "";
        await editor.commit(content, connection.host, connection.authToken ?? "");
    }

    async function handleAbort() {
        await editor.abort(connection.host, connection.authToken ?? "");
    }

    // Kick off the content fetch once on mount.
    onMount(async () => {
        const s = editor.session;
        if (!s) return;
        initialContent = await editor.loadInitialContent(connection.host, connection.authToken ?? "");
        loading = false;
    });

    // Initialize CodeMirror as soon as editorEl is in the DOM and loading is done.
    // $effect re-runs when either changes; the `if (view)` guard prevents double-init.
    $effect(() => {
        if (loading || !editorEl || view) return;
        const s = editor.session;
        if (!s) return;

        view = new EditorView({
            state: EditorState.create({
                doc: initialContent,
                extensions: makeExtensions(s.target),
            }),
            parent: editorEl,
        });
        view.focus();
    });

    onDestroy(() => {
        view?.destroy();
        view = null;
    });
</script>

<div class="verb-editor">
    <div class="editor-header">
        <span class="orn">◈</span>
        <span class="editor-title">{editor.session?.title ?? "Editor"}</span>
        <span class="orn">◈</span>
    </div>
    <div class="editor-rule"></div>

    <div class="editor-body">
        <div class="editor-cm" bind:this={editorEl}></div>
        {#if loading}
            <div class="editor-loading">Loading…</div>
        {/if}
    </div>

    <div class="editor-rule"></div>
    <div class="editor-footer">
        <button
            class="btn btn-commit"
            onclick={handleCommit}
            disabled={editor.status === "saving"}
            title="Ctrl+Enter"
        >
            {editor.status === "saving" ? "Saving…" : "COMMIT"}
        </button>
        <button
            class="btn btn-abort"
            onclick={handleAbort}
            disabled={editor.status === "saving"}
            title="Escape"
        >
            ABORT
        </button>
        {#if editor.statusMessage}
            <span class="status-msg" class:is-error={editor.status === "error"}>
                {editor.statusMessage}
            </span>
        {/if}
        <span class="kbd-hint">^↵ commit · esc abort</span>
    </div>
</div>

<style>
    .verb-editor {
        position: relative;
        display: flex;
        flex-direction: column;
        width: 100%;
        aspect-ratio: 16 / 9;
        background: rgba(5, 7, 4, 0.97);
        border: 2px solid #6b5520;
        box-shadow:
            0 0 0 1px #1a1208,
            0 0 0 3px #4a3810,
            0 3px 14px rgba(0, 0, 0, 0.85);
        overflow: hidden;
        font-family: 'Courier New', Courier, monospace;
        flex-shrink: 0;
    }

    /* scanline overlay */
    .verb-editor::before {
        content: '';
        position: absolute;
        inset: 0;
        background: repeating-linear-gradient(
            0deg,
            rgba(0, 0, 0, 0) 0px,
            rgba(0, 0, 0, 0) 3px,
            rgba(0, 0, 0, 0.12) 3px,
            rgba(0, 0, 0, 0.12) 4px
        );
        pointer-events: none;
        z-index: 10;
    }

    /* inner border inset */
    .verb-editor::after {
        content: '';
        position: absolute;
        inset: 5px;
        border: 1px solid rgba(110, 85, 22, 0.12);
        pointer-events: none;
        z-index: 10;
    }

    .editor-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 0.55rem;
        letter-spacing: 0.18em;
        color: #8a7040;
        text-transform: uppercase;
        padding: 0.3rem 0.6rem 0.2rem;
        flex-shrink: 0;
        position: relative;
        z-index: 5;
    }

    .editor-title {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 80%;
        color: #a88840;
    }

    .orn {
        color: #5a4520;
        font-size: 0.48rem;
        flex-shrink: 0;
    }

    .editor-rule {
        height: 1px;
        background: linear-gradient(
            90deg,
            transparent,
            #5b4520 15%,
            #7a6228 50%,
            #5b4520 85%,
            transparent
        );
        flex-shrink: 0;
        position: relative;
        z-index: 5;
    }

    .editor-body {
        flex: 1 1 0;
        min-height: 0;
        overflow: hidden;
        position: relative;
        z-index: 5;
    }

    .editor-cm {
        height: 100%;
        overflow: hidden;
    }

    /* override CodeMirror root to fill */
    .editor-cm :global(.cm-editor) {
        height: 100%;
    }
    .editor-cm :global(.cm-scroller) {
        overflow: auto;
    }

    .editor-loading {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(8, 6, 3, 0.92);
        color: #5a4828;
        font-size: 0.65rem;
        letter-spacing: 0.15em;
        text-transform: uppercase;
        z-index: 6;
    }

    .editor-footer {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.25rem 0.5rem;
        flex-shrink: 0;
        position: relative;
        z-index: 5;
    }

    .btn {
        font-family: 'Courier New', Courier, monospace;
        font-size: 0.55rem;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        padding: 0.18rem 0.5rem;
        border: 1px solid;
        cursor: pointer;
        background: transparent;
        transition: background 0.15s, color 0.15s;
    }

    .btn:disabled {
        opacity: 0.45;
        cursor: default;
    }

    .btn-commit {
        color: #c8a840;
        border-color: #6b5520;
    }
    .btn-commit:hover:not(:disabled) {
        background: rgba(200, 168, 64, 0.14);
    }

    .btn-abort {
        color: #805838;
        border-color: #503820;
    }
    .btn-abort:hover:not(:disabled) {
        background: rgba(128, 88, 56, 0.14);
    }

    .status-msg {
        font-size: 0.55rem;
        color: #9a8840;
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .status-msg.is-error {
        color: #a05030;
    }

    .kbd-hint {
        font-size: 0.48rem;
        color: #3a2c14;
        letter-spacing: 0.08em;
        margin-left: auto;
        white-space: nowrap;
    }
</style>
